import React, { useState } from "react";
import { GREENFIELD_TIME_BUCKETS } from "../bridge/buildGreenfieldFiltersViewModel";
import "./FlightFilterSidebar.css";

/**
 * Discrete V1 time buckets only — never imply hour-level filtering.
 */
function TimeBucketSeg({ options, ariaLabel, onSelect }) {
  const opts =
    Array.isArray(options) && options.length > 0
      ? options
      : GREENFIELD_TIME_BUCKETS.map((b) => ({ ...b, checked: false }));
  return (
    <div className="fe2-fsb__seg" role="group" aria-label={ariaLabel}>
      {opts.map((opt) => {
        const pressed = Boolean(opt.checked);
        return (
          <button
            key={opt.id}
            type="button"
            className={
              pressed ? "fe2-fsb__seg-btn fe2-fsb__seg-btn--on" : "fe2-fsb__seg-btn"
            }
            aria-pressed={pressed}
            onClick={() => onSelect?.(opt.id)}
          >
            <span className="fe2-fsb__seg-label">{opt.label}</span>
            {opt.rangeLabel ? (
              <span className="fe2-fsb__seg-range fe2-ltr">{opt.rangeLabel}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Greenfield filter sidebar — presentation only.
 * Actions bubble via onFilterAction → existing V1 filter state (no new filter engine).
 */
export default function FlightFilterSidebar({ filters, onFilterAction }) {
  const data = filters || {};
  const emit = (action) => {
    if (typeof onFilterAction === "function") onFilterAction(action);
  };
  const controlled = typeof onFilterAction === "function";
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <aside className={mobileOpen ? "fe2-fsb fe2-fsb--open" : "fe2-fsb"}>
      <header className="fe2-fsb__head">
        <h2 className="fe2-fsb__title" id="fe2-fsb-title">
          סינון תוצאות
        </h2>
        <button
          type="button"
          className="fe2-fsb__toggle"
          aria-expanded={mobileOpen}
          aria-controls="fe2-fsb-body"
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? "הסתר סינון" : "הצג סינון"}
        </button>
        <button
          type="button"
          className="fe2-fsb__clear"
          onClick={() => emit({ type: "clear" })}
        >
          נקה הכל
        </button>
      </header>
      <div className="fe2-fsb__body" id="fe2-fsb-body">

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">עצירות</h3>
        <ul className="fe2-fsb__list">
          {(data.stops || []).map((opt) => (
            <li key={opt.id}>
              <label className="fe2-fsb__row">
                <span className="fe2-fsb__check-wrap">
                  <input
                    type="checkbox"
                    checked={controlled ? Boolean(opt.checked) : undefined}
                    defaultChecked={controlled ? undefined : opt.checked}
                    onChange={(e) =>
                      emit({
                        type: "toggleStop",
                        id: opt.id,
                        checked: e.target.checked,
                      })
                    }
                  />
                  <span>{opt.label}</span>
                </span>
                <span className="fe2-fsb__count fe2-ltr">{opt.count}</span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">חברות תעופה</h3>
        <ul className="fe2-fsb__list">
          {(data.airlines || []).map((opt) => (
            <li key={opt.id}>
              <label className="fe2-fsb__row">
                <span className="fe2-fsb__check-wrap">
                  <input
                    type="checkbox"
                    checked={controlled ? Boolean(opt.checked) : undefined}
                    defaultChecked={controlled ? undefined : opt.checked}
                    onChange={(e) =>
                      emit({
                        type: "toggleAirline",
                        id: opt.id,
                        checked: e.target.checked,
                      })
                    }
                  />
                  <span className="fe2-fsb__airline-logo" aria-hidden>
                    {opt.id}
                  </span>
                  <span>{opt.label}</span>
                </span>
                <span className="fe2-fsb__count fe2-ltr">{opt.count}</span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">שעות יציאה</h3>
        <TimeBucketSeg
          options={data.outboundTime?.options}
          ariaLabel="שעות יציאה"
          onSelect={(id) => emit({ type: "toggleOutboundTime", id })}
        />
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">שעות חזרה</h3>
        <TimeBucketSeg
          options={data.returnTime?.options}
          ariaLabel="שעות חזרה"
          onSelect={(id) => emit({ type: "toggleReturnTime", id })}
        />
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">משך טיסה</h3>
        <div className="fe2-fsb__slider">
          <input
            type="range"
            min={data.duration?.min ?? 0}
            max={Math.max(1, Math.ceil((data.duration?.maxMinutes ?? 900) / 60))}
            value={
              controlled
                ? Math.ceil((data.duration?.valueMinutes ?? 0) / 60)
                : undefined
            }
            defaultValue={controlled ? undefined : 8}
            aria-label="משך טיסה"
            onChange={(e) =>
              emit({
                type: "durationMax",
                value: Number(e.target.value) * 60,
              })
            }
          />
          <div className="fe2-fsb__slider-labels">
            <span>0 שע׳</span>
            <span>
              {Math.max(1, Math.ceil((data.duration?.maxMinutes ?? 900) / 60))} שע׳
            </span>
          </div>
        </div>
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">כבודה</h3>
        <ul className="fe2-fsb__list">
          {(data.baggage || []).map((opt) => (
            <li key={opt.id}>
              <label className="fe2-fsb__row">
                <span className="fe2-fsb__check-wrap">
                  <input
                    type="checkbox"
                    checked={controlled ? Boolean(opt.checked) : undefined}
                    defaultChecked={controlled ? undefined : opt.checked}
                    onChange={(e) =>
                      emit({
                        type: "toggleBaggage",
                        id: opt.id,
                        checked: e.target.checked,
                      })
                    }
                  />
                  <span>{opt.label}</span>
                </span>
                <span className="fe2-fsb__count fe2-ltr">{opt.count}</span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="fe2-fsb__section">
        <h3 className="fe2-fsb__section-title">מחיר</h3>
        <div className="fe2-fsb__slider">
          <input
            type="range"
            min={data.price?.min ?? 0}
            max={Math.max(data.price?.min ?? 0, data.price?.max ?? 500)}
            value={controlled ? data.price?.value ?? data.price?.max ?? 0 : undefined}
            defaultValue={controlled ? undefined : 350}
            aria-label="מחיר מקס׳"
            onChange={(e) =>
              emit({ type: "priceMax", value: Number(e.target.value) })
            }
          />
          <div className="fe2-fsb__slider-labels fe2-ltr">
            <span>${data.price?.min ?? 100}</span>
            <span>${data.price?.max ?? 500}</span>
          </div>
        </div>
      </section>
      </div>
    </aside>
  );
}
